// Cross-viewport sync bus for multi-frame playback.
//
// Three sync modes per spec:
//   - UNSYNCHRONIZED   : each viewport plays independently (default)
//   - PLAY_SYNC_START  : Play in one viewport aligns all others to the same
//                       frame index + fps and starts them
//   - SYNC_PLAYBACK    : Play in one viewport starts all others, but each
//                       plays at its own native fps from its current frame

export const SYNC_MODES = {
  UNSYNCHRONIZED: "unsynchronized",
  PLAY_SYNC_START: "playSyncStart",
  SYNC_PLAYBACK: "syncPlayback",
};

const SYNC_ORDER = [
  SYNC_MODES.UNSYNCHRONIZED,
  SYNC_MODES.PLAY_SYNC_START,
  SYNC_MODES.SYNC_PLAYBACK,
];

const SYNC_LABELS = {
  [SYNC_MODES.UNSYNCHRONIZED]: "Unsynchronized",
  [SYNC_MODES.PLAY_SYNC_START]: "Play sync start",
  [SYNC_MODES.SYNC_PLAYBACK]: "Sync playback",
};

export const getSyncModeLabel = (mode) => SYNC_LABELS[mode] || mode;

let _mode = SYNC_MODES.UNSYNCHRONIZED;
const modeListeners = new Set();

const emitMode = () => {
  modeListeners.forEach((fn) => {
    try {
      fn(_mode);
    } catch (err) {
      console.debug("[MultiFrame] sync listener threw:", err);
    }
  });
};

export const getSyncMode = () => _mode;

export const setSyncMode = (mode) => {
  if (_mode === mode || !SYNC_ORDER.includes(mode)) return;
  _mode = mode;
  emitMode();
};

export const cycleSyncMode = () => {
  const i = SYNC_ORDER.indexOf(_mode);
  setSyncMode(SYNC_ORDER[(i + 1) % SYNC_ORDER.length]);
  return _mode;
};

export const subscribeSyncMode = (listener) => {
  modeListeners.add(listener);
  listener(_mode);
  return () => modeListeners.delete(listener);
};

// Cross-engine broadcast. Engines that have subscribed react to events
// emitted by OTHER viewports according to the current sync mode.
const broadcasters = new Set();

export const subscribeBroadcast = (handler) => {
  broadcasters.add(handler);
  return () => broadcasters.delete(handler);
};

const broadcast = (sourceViewportId, action, payload) => {
  const event = { sourceViewportId, action, payload, mode: _mode };
  broadcasters.forEach((h) => {
    try {
      h(event);
    } catch (err) {
      console.debug("[MultiFrame] broadcast handler threw:", err);
    }
  });
};

export const broadcastPlay = (sourceViewportId, fps, frame) =>
  broadcast(sourceViewportId, "play", { fps, frame });

export const broadcastPause = (sourceViewportId) =>
  broadcast(sourceViewportId, "pause", {});

export const broadcastSeek = (sourceViewportId, frame) =>
  broadcast(sourceViewportId, "seek", { frame });
